import React, {
    createContext,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useSelector } from 'react-redux';
import { fetchDocumentData } from 'pages/SystemMessages/utils';
import { useTranslation } from 'react-i18next';
import { notification } from 'ui-library';
import { getFileFromBase64 } from 'utils/file';
import { isNowBetween } from 'utils/datetime';
import { getSetupData } from 'additiv-services/setup';
import { memberIdSelector } from '../../redux-store/auth/authSelectors';
import { useGenericAPIMutation } from '../../hooks/mutations/useGenericAPIMutation';
import { useGetCustomService } from '../../hooks/rest/useGetCustomService';

const setupData = getSetupData();

type TActiveSystemMessageContext = {
    hasActiveSystemMessage: boolean;
    activeSystemMessageData: any;
    isMarkedAsRead: boolean;
    markAsRead?: (messageId: number) => Promise<void>;
    isLoading?: boolean;
    error?: any;
    isMutationLoading?: boolean;
    isPreviewActive?: boolean;
    setPreviewMessageStatus?: (val: boolean) => void;
    refetchActiveSystemMessage?: () => void;
};

type TActiveSystemMessageProvider = {
    clientApplication?: number;
    children: React.JSX.Element;
};

const ActiveSystemMessageContext = createContext<TActiveSystemMessageContext>({
    hasActiveSystemMessage: false,
    activeSystemMessageData: null,
    isMarkedAsRead: false,
    isLoading: false,
    error: null,
    isMutationLoading: false,
});

const STALE_TIME = 1000 * 60 * 10; // 10 minutes
const GC_TIME = 1000 * 60 * 15; // 15 minutes

const ActiveSystemMessageProviderInner = (props: TActiveSystemMessageProvider) => {
    const { clientApplication = 1, children } = props;
    const {
        t,
        i18n: { language },
    } = useTranslation();

    const personId = useSelector(memberIdSelector);
    const [isPreviewActive, setIsPreviewActive] = useState(false);
    const [fileTypeDocument, setFileTypeDocument] = useState<any>(null);

    const [messageDocumentLoading, setMessageDocumentLoading] = useState(false);
    const [activeMessageDocumentData, setActiveMessageDocumentData] = useState<Record<
        string,
        any
    > | null>(null);

    const setPreviewMessageStatus = useCallback((val: boolean) => {
        setIsPreviewActive(val);
    }, []);

    const {
        data: activeSystemMessageDataFromAPI,
        isLoading,
        error,
        refetch: refetchQuery,
    } = useGetCustomService({
        service: 'customSystemMessageService',
        api: 'getActiveSystemMessageDetails',
        payload: { personId, clientApplication, language },
        enabled: !!personId && !!clientApplication,
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
    });

    const { mutateAsync: handleMutation, isPending: isMutationLoading } = useGenericAPIMutation();

    const refetchActiveSystemMessage = useCallback(async () => {
        await refetchQuery();
    }, [refetchQuery]);

    const activeSystemMessageDataSource = activeSystemMessageDataFromAPI?.data;

    const getMessageDocument = useCallback(async () => {
        if (activeMessageDocumentData?.[language] || !activeSystemMessageDataSource?.uId) return;

        setMessageDocumentLoading(true);

        try {
            const response: any = await handleMutation({
                service: 'documents',
                API: 'postDocumentsList',
                payload: {
                    page: 1,
                    pageSize: 10,
                    contractId: activeSystemMessageDataSource?.uId,
                    isActive: true,
                    languageTwoLetterIsoCode: language,
                },
            });

            if (response?.data?.results?.length === 0) {
                return;
            }

            const documents = await fetchDocumentData({
                documents: response?.data?.results || [],
                getRawData: true,
            });

            const documentData = {
                file: documents?.[language]?.file,
                filename: documents?.[language]?.filename,
                mimeType: documents?.[language]?.mimeType,
            };

            setActiveMessageDocumentData((prev) => ({
                ...prev,
                [language]: documentData,
            }));
        } catch (error) {
            notification.open({
                content: t('general.somethingWentWrong'),
                type: 'error',
            });
        } finally {
            setMessageDocumentLoading(false);
        }
    }, [
        language,
        activeSystemMessageDataSource?.uId,
    ]);

    const markAsRead = useCallback(
        async (messageId: number) => {
            await handleMutation({
                service: 'customSystemMessageService',
                API: 'markSystemMessageAsRead',
                payload: { personId, messageId },
                invalidateQueryKeys: ['getData_getActiveSystemMessageDetails'],
            });
        },
        [personId, handleMutation],
    );

    useEffect(() => {
        if (activeSystemMessageDataSource?.uId && !activeMessageDocumentData?.[language]) {
            getMessageDocument();
        }
    }, [activeSystemMessageDataSource?.uId, getMessageDocument, activeMessageDocumentData, language]);

    const nowTimeIsBetweenMessageTimeRange = useMemo(() => {
        if (!activeSystemMessageDataSource) return true;

        const { startDateTime, endDateTime } = activeSystemMessageDataSource;

        return isNowBetween({ startDate: startDateTime, endDate: endDateTime });
    }, [activeSystemMessageDataSource]);

    const hasActiveSystemMessage = useMemo(() => (
        !!activeSystemMessageDataSource && nowTimeIsBetweenMessageTimeRange
    ), [activeSystemMessageDataSource, nowTimeIsBetweenMessageTimeRange]);

    const activeSystemMessageData = useMemo(
        () => ({ ...activeSystemMessageDataSource }),
        [activeSystemMessageDataSource],
    );
    const isMarkedAsRead = activeSystemMessageDataSource?.isRead || false;

    useEffect(() => {
        let cancelled = false;

        (async () => {
            if (activeMessageDocumentData?.[language]) {
                const { file, filename, mimeType } = activeMessageDocumentData[language];

                try {
                    const fileFromBase64 = await getFileFromBase64(
                        file,
                        filename,
                        mimeType,
                    );

                    if (!cancelled) setFileTypeDocument(fileFromBase64);
                } catch (error) {
                    if (!cancelled) {
                        notification.open({
                            content: t('general.somethingWentWrong'),
                            type: 'error',
                        });
                    }
                }
            }
        })();

        return () => { cancelled = true; };
    }, [activeMessageDocumentData?.[language], language]);

    const messageDataForPreview = useMemo(
        () => ({
            ...activeSystemMessageData,
            document: fileTypeDocument,
        }),
        [activeSystemMessageData, fileTypeDocument],
    );

    const state = useMemo(
        () => ({
            hasActiveSystemMessage,
            activeSystemMessageData: messageDataForPreview,
            isMarkedAsRead,
            markAsRead,
            isPreviewActive,
            setPreviewMessageStatus,
            isLoading: isLoading || messageDocumentLoading,
            error,
            isMutationLoading,
            refetchActiveSystemMessage,
        }),
        [
            hasActiveSystemMessage,
            messageDataForPreview,
            isMarkedAsRead,
            markAsRead,
            isPreviewActive,
            isLoading,
            messageDocumentLoading,
            error,
            isMutationLoading,
            refetchActiveSystemMessage,
            setPreviewMessageStatus,
        ],
    );

    return (
        <ActiveSystemMessageContext.Provider
            value={state}
        >
            {children}
        </ActiveSystemMessageContext.Provider>
    );
};

const ActiveSystemMessageProvider = (props: TActiveSystemMessageProvider) => {
    if (!setupData?.SYSTEM_MESSAGE_FEATURE_FLAG) {
        return <>{props.children}</>;
    }

    return <ActiveSystemMessageProviderInner {...props} />;
};

export { ActiveSystemMessageContext, ActiveSystemMessageProvider };
